"use client";

import { useEffect, useState } from "react";
import { logDataDiag } from "@/lib/dataDiagnostics";
import YourBarrioLoader from "@/components/ui/YourBarrioLoader";

const REMOVE_DELAY_MS = 460;
const STANDALONE_MIN_VISIBLE_MS = 180;
const SPLASH_DISMISS_DEADLINE_MS = 3000;

function detectStandaloneMode() {
  if (typeof window === "undefined") {
    return false;
  }

  if (window.__ybStandaloneMode === true) {
    return true;
  }

  try {
    return (
      window.matchMedia?.("(display-mode: standalone)")?.matches ||
      window.navigator.standalone === true
    );
  } catch {
    return false;
  }
}

function markBootReady(source = "react") {
  if (typeof window !== "undefined" && typeof window.__ybMarkBootReady === "function") {
    window.__ybMarkBootReady(source);
    return;
  }

  document.documentElement.classList.add("yb-app-ready");
  document.documentElement.classList.remove("yb-app-booting");
}

export default function AppBootSplash() {
  const [state, setState] = useState("visible");

  useEffect(() => {
    window.__ybPwaStartup?.markHydrated?.();
    const standalone = detectStandaloneMode();
    let frameOne = 0;
    let frameTwo = 0;
    let removeTimer = 0;
    let readyTimer = 0;
    let deadlineTimer = 0;
    let dismissed = false;

    const hide = (dismissalReason = "unknown") => {
      if (dismissed) return;
      dismissed = true;
      if (window.__ybBootSplashTimer) {
        window.clearTimeout(window.__ybBootSplashTimer);
        window.__ybBootSplashTimer = 0;
      }

      markBootReady("react");

      const bootStartedAt =
        typeof window.__ybBootStartedAt === "number" ? window.__ybBootStartedAt : 0;
      const durationMs =
        window.performance && typeof window.performance.now === "function"
          ? Math.max(0, Math.round(window.performance.now() - bootStartedAt))
          : null;
      try {
        const readyAt = window.performance.now();
        window.performance.mark("yb-app-ready");
        window.performance.measure("yb-app-boot", {
          start: bootStartedAt,
          end: readyAt,
        });
      } catch {}
      logDataDiag("app:boot_ready", {
        durationMs,
        standalone,
        dismissalReason,
      });
      window.dispatchEvent(
        new CustomEvent("yb:app-ready", {
          detail: { durationMs, standalone, dismissalReason },
        })
      );

      setState("hidden");
      removeTimer = window.setTimeout(() => {
        setState("removed");
      }, REMOVE_DELAY_MS);
    };

    if (!standalone) {
      frameOne = window.requestAnimationFrame(() => {
        if (window.__ybBootSplashTimer) {
          window.clearTimeout(window.__ybBootSplashTimer);
          window.__ybBootSplashTimer = 0;
        }
        markBootReady("react_browser");
        setState("removed");
      });

      return () => {
        window.cancelAnimationFrame(frameOne);
        window.clearTimeout(removeTimer);
      };
    }

    deadlineTimer = window.setTimeout(
      () => hide("react_deadline"),
      SPLASH_DISMISS_DEADLINE_MS
    );

    const hideAfterPaint = () => {
      const bootStartedAt =
        typeof window.__ybBootStartedAt === "number" ? window.__ybBootStartedAt : 0;
      const elapsed =
        window.performance && typeof window.performance.now === "function"
          ? window.performance.now() - bootStartedAt
          : STANDALONE_MIN_VISIBLE_MS;
      const remaining = Math.max(0, STANDALONE_MIN_VISIBLE_MS - elapsed);

      readyTimer = window.setTimeout(() => {
        frameOne = window.requestAnimationFrame(() => {
          frameTwo = window.requestAnimationFrame(() =>
            hide("hydrated_after_paint")
          );
        });
      }, remaining);
    };

    hideAfterPaint();

    return () => {
      window.cancelAnimationFrame(frameOne);
      window.cancelAnimationFrame(frameTwo);
      window.clearTimeout(readyTimer);
      window.clearTimeout(removeTimer);
      window.clearTimeout(deadlineTimer);
    };
  }, []);

  if (state === "removed") {
    return null;
  }

  return (
    <div
      id="yb-boot-splash"
      role="status"
      aria-label="Loading YourBarrio"
      data-state={state}
      suppressHydrationWarning
    >
      <div className="yb-boot-splash__inner" aria-hidden="true">
        <img
          className="yb-boot-splash__logo"
          src="/brand/logo-mark-white.svg"
          alt=""
          width="272"
          height="272"
          decoding="sync"
          fetchPriority="high"
        />
        <YourBarrioLoader
          size="md"
          className="yb-boot-splash__loader"
          style={{ color: "#ffffff" }}
        />
      </div>
    </div>
  );
}
